import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { CurrentPositionViewModel } from "../models/pnl/current-position-view-model";
import { IComponent } from "./component.interface";
import { ExitOrderPlotLineComponent } from "./exit-order-plot-line.component";
import { InformationPopupComponent } from "./information-popup.component";
import { PnlComponent } from "./pnl.component";

export class DrawExitOrderComponent implements IComponent {
    public id = '';

    public selectedPlotLine: ExitOrderPlotLineComponent | null = null;
    public removeOcaGroups: Array<string> = [];
    public requestingProfitTarget = false;
    public requestingStopLoss = false;

    private base: IChartContainer;

    private tradeOrders: Array<TradeOrderDataModel> = [];
    private currentPositions: Array<CurrentPositionViewModel> = [];
    private chartStyle: ChartStyleSettingsModel | null = null;
    private canDraw = false;
    private isRemovingForSwitchingSymbol = false;
    private drawTimer: any;
    // private previouseTradeUpdate: Date | null = null;

    constructor(base: IChartContainer) {
        this.base = base;
        this.subscribeChartDataService();
        this.onResize();
    }

    public remove() {
        this.isRemovingForSwitchingSymbol = true;
        this.removePlotLine();
        this.currentPositions = [];
        this.removeOcaGroups = [];
        this.isRemovingForSwitchingSymbol = false;
        this.requestingProfitTarget = false;
        this.requestingStopLoss = false;
        this.disable();
    }

    public enable() {
        if (this.canDraw) return;

        this.canDraw = true;
        // this.update();
    }

    public disable() {
        this.canDraw = false;
        // this.update();
    }

    private subscribeChartDataService() {
        this.base.chartService.subscribeDrawTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            this.tradeOrders = tradeOrders;
            this.displayProfitTargetAndStopLoss();
        });
        this.base.chartService?.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.chartStyle = chartStyle;
        });
        this.base.chartService?.subscribeCurrentPositionViewModel((currentPositions: Array<CurrentPositionViewModel>) => {

            if (this.isRemovingForSwitchingSymbol) {
                return;
            }

            this.currentPositions = currentPositions;
            // if (!this.previouseTradeUpdate) {
            //     this.previouseTradeUpdate = new Date();
            // }

            this.update();
            // this.previouseTradeUpdate = new Date();
        });
    }

    public update(forceHide?: boolean) {
        if (!this.canDraw || !this.chartStyle) return;

        if (forceHide) {
            this.removePlotLine();
            this.removeLastPriceElements();
        }

        clearTimeout(this.drawTimer);
        this.drawTimer = setTimeout(() => {
            const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
            if (this.selectedPlotLine?.mouseHover || this.base.tiChartSeries.preventDragging || popup.isActive()) {
                return;
            }

            this.cancelStopLossOrProfitTargetIfFilled();

            this.removePlotLine();
            this.createPlotLines();
        }, 150);
    }

    private cancelStopLossOrProfitTargetIfFilled() {
        if (!this.selectedPlotLine) {
            return;
        }

        const positionExist = this.currentPositions.find(x => x.accountId == this.selectedPlotLine.model.accountId && x.shares > 0);
        if (positionExist) {
            return;
        }

        const tradeOrders = this.getTradeOrders().filter(x => x.statusText == 'filled');
        for (const t of tradeOrders) {
            let tradeType = '';
            if (t.tradeType == 'limit') {
                tradeType = 'stop';
            } else if (t.tradeType == 'stop') {
                tradeType = 'limit';
            }

            const relatedOrders = this.getTradeOrders().filter(x =>
                x.tradeType == tradeType &&
                x.statusText == 'submitted');

            for (const relatedOrder of relatedOrders) {
                relatedOrder.statusText = 'canceled';
                this.base.chartService.requestRemoveChartTrade(relatedOrder.id);
            }
        }
    }

    private removePlotLine() {
        if (this.selectedPlotLine) {
            this.selectedPlotLine.remove();
            this.selectedPlotLine = null;
        }
    }

    private removeLastPriceElements() {
        this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.exit-order').forEach((el: HTMLElement) => {
            el.remove();
        });
        this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.profit-target').forEach((el: HTMLElement) => {
            el.remove();
        });
        this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.stop-loss').forEach((el: HTMLElement) => {
            el.remove();
        });
    }

    private createPlotLines() {
        const pnlComponent = this.base.getComponentsByType(PnlComponent)?.[0] as PnlComponent;
        let selectedAccount = '';
        if (pnlComponent) {
            selectedAccount = pnlComponent.selectedAccount;
        }

        let positionToAdd: CurrentPositionViewModel = null;
        for (const position of this.currentPositions) {
            if (position.shares != 0 && selectedAccount == position.accountName) {
                positionToAdd = position;
                break;
            }
        }

        if (positionToAdd) {
            this.selectedPlotLine = new ExitOrderPlotLineComponent(this.base, this, positionToAdd);
            this.displayProfitTargetAndStopLoss();
        } else {
            this.removeLastPriceElements();
        }
    }

    public displayProfitTargetAndStopLoss() {
        if (!this.selectedPlotLine || this.base.tiChartSeries.preventDragging) return;

        let profitTarget: TradeOrderDataModel | null = null;
        let stopLoss: TradeOrderDataModel | null = null;
        if (this.selectedPlotLine.isLong) {
            profitTarget = this.getOrder_LongProfitTarget();
            stopLoss = this.getOrder_LongStopLoss();
        } else {
            profitTarget = this.getOrder_ShortProfitTarget();
            stopLoss = this.getOrder_ShortStopLoss();
        }

        if (profitTarget && this.removeOcaGroups.indexOf(profitTarget.ocaGroup) == -1) {
            this.selectedPlotLine.createProfitTargetFromTradeOrder(profitTarget);

            this.requestingProfitTarget = false;
        } else {
            this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.profit-target').forEach((el: HTMLElement) => {
                el.remove();
            });
        }

        if (stopLoss && this.removeOcaGroups.indexOf(stopLoss.ocaGroup) == -1) {
            this.selectedPlotLine.createStopLossFromTradeOrder(stopLoss);

            this.requestingStopLoss = false;
        } else {
            this.base.chartWrapper.querySelectorAll<HTMLElement>('.last-price-wrapper.stop-loss').forEach((el: HTMLElement) => {
                el.remove();
            });
        }
    }

    private getOrder_LongProfitTarget(): TradeOrderDataModel | null {
        const tradeOrder = this.getTradeOrders().find(x =>
            x.tradeType == 'limit' &&
            !x.isLong &&
            x.statusText === 'submitted');
        return tradeOrder;
    }

    private getOrder_LongStopLoss(): TradeOrderDataModel | null {
        const tradeOrder = this.getTradeOrders().find(x =>
            x.tradeType == 'stop' &&
            !x.isLong &&
            x.statusText === 'submitted');
        return tradeOrder;
    }

    private getOrder_ShortProfitTarget(): TradeOrderDataModel | null {
        const tradeOrder = this.getTradeOrders().find(x =>
            x.tradeType == 'limit' &&
            x.isLong &&
            x.statusText === 'submitted');
        return tradeOrder;
    }

    private getOrder_ShortStopLoss(): TradeOrderDataModel | null {
        const tradeOrder = this.getTradeOrders().find(x =>
            x.tradeType == 'stop' &&
            x.isLong &&
            x.statusText === 'submitted');
        return tradeOrder;
    }

    private getTradeOrders(): Array<TradeOrderDataModel> {
        if (!this.selectedPlotLine?.model) return [];

        const m = this.selectedPlotLine.model;
        const ocaGroup = m.getOCAGroupPrefix();
        const tradeOrders = this.tradeOrders.filter(x => x.accountName == m.accountName && x.ocaGroup.indexOf(ocaGroup) > -1);
        return tradeOrders;
    }

    private resizeTimmer = null as any;
    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.update();
            }, 150);
        });
    }
}